import { SessionExpiredBanner } from '@/components/session-expired-banner'
import { ErrorAlert } from '@/components/ui/error-alert'
import { MaintenanceMode } from '@/components/ui/maintenance-mode'
import { OpenInNewTab } from '@/components/ui/open-in-new-tab'
import { PageLoader } from '@/components/ui/page-loader'
import { Toaster } from 'sonner'
import '@/index.css'
import { isMaintenanceMode } from '@/lib/env'
import { DtoolsSiImportRecovery } from '@/lib/dtools-si-import-recovery-component'
import { trackException } from '@/lib/telemetry'
import { msalInstance } from '@/lib/msal-config'
import { DiscountPlugin } from '@/lib/plugins/discount-plugin'
import { InvalidatePlugin } from '@/lib/plugins/invalidate-plugin'
import { LaborPlugin } from '@/lib/plugins/labor-plugin'
import { PaymentMilestonePlugin } from '@/lib/plugins/payment-milestone-plugin'
import { QuotePlugin } from '@/lib/plugins/quote-plugin'
import { SuppliesPlugin } from '@/lib/plugins/supplies-plugin'
import { TaxRecalculationPlugin } from '@/lib/plugins/tax-recalculation-plugin'
import { queryClient } from '@/lib/query-client'
import { useProposalDataSync } from '@/lib/proposal-data-sync'
import { deriveErrorMessage, isInIframe } from '@/lib/utils'
import { AuthenticatedTemplate, MsalProvider } from '@azure/msal-react'
import { QueryClientProvider } from '@tanstack/react-query'
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router'
import { Fragment, memo, useEffect, useRef, useState } from 'react'
import { z } from 'zod'
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

/**
 * The toast's inline buttons, as the app draws every other small button: a
 * full-round pill (`Button` `size="sm"`, `variant="default"`), not sonner's
 * 4px/24px/12px default — the Undo on a room move is the app's only safety net
 * for a destructive-adjacent action and it read as a foreign control.
 *
 * The `!`s are not decoration. Sonner styles its own buttons at
 * `[data-sonner-toast][data-styled='true'] [data-button]` — three attribute
 * selectors, which outranks any single utility class — so EVERY property it sets
 * there (border-radius, height, padding-left/right, font-size, color,
 * background) has to be reclaimed, and the `:focus-visible` box-shadow it sets
 * one selector higher again. Properties sonner leaves alone (`gap`,
 * `transition-colors`) take plain utilities. `richColors` only paints the toast
 * body and its close button, so an error toast stays red around a primary
 * action.
 */
const TOAST_ACTION_BUTTON =
  'h-7! gap-1 rounded-full! bg-primary! px-2.5! text-[0.8rem]! font-medium text-primary-foreground! transition-colors hover:bg-primary/90! focus-visible:ring-3! focus-visible:ring-ring/50!'
const TOAST_CANCEL_BUTTON =
  'h-7! gap-1 rounded-full! bg-muted! px-2.5! text-[0.8rem]! font-medium text-foreground! transition-colors hover:bg-muted/80! focus-visible:ring-3! focus-visible:ring-ring/50!'

const Root = memo(function Root() {
  // Validate quote id exists so we can use getQuoteId @/utils function
  const { id: quoteId } = Route.useSearch()
  useProposalDataSync(quoteId ?? '')
  const [isInitializing, setIsInitializing] = useState(true)

  // Wait for at least .5 seconds for the msal instance to redirect back to url with quote id query param before throwing missing quote id error
  useEffect(() => {
    if (quoteId) {
      setIsInitializing(false)
    } else {
      setIsInitializing(true)
    }

    const timeout = setTimeout(() => {
      setIsInitializing(false)
    }, 500)

    return () => clearTimeout(timeout)
  }, [quoteId])

  if (isInIframe) {
    return <OpenInNewTab />
  }

  if (isMaintenanceMode) {
    return <MaintenanceMode />
  }

  if (isInitializing && !quoteId) {
    return <PageLoader message="Initializing..." />
  }

  if (!quoteId) {
    return <ErrorAlert title="Error loading CPQ" message="No quote ID provided." />
  }

  return (
    <Fragment>
      <Outlet />
      <SessionExpiredBanner />
      <Toaster
        richColors
        position="bottom-right"
        className="print:hidden"
        toastOptions={{ classNames: { description: 'whitespace-pre-line', actionButton: TOAST_ACTION_BUTTON, cancelButton: TOAST_CANCEL_BUTTON } }}
      />

      <AuthenticatedTemplate>
        <DtoolsSiImportRecovery quoteId={quoteId} />
        <LaborPlugin />
        <SuppliesPlugin />
        <QuotePlugin />
        <TaxRecalculationPlugin />
        <PaymentMilestonePlugin />
        <DiscountPlugin />
        <InvalidatePlugin />
        {/* {import.meta.env.MODE === 'development' && <ReactQueryDevtools />} */}
      </AuthenticatedTemplate>
    </Fragment>
  )
})

const RootRoute = memo(function RootRoute() {
  return (
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <Root />
      </QueryClientProvider>
    </MsalProvider>
  )
})

const RootErrorComponent = memo(function RootErrorComponent({ error }: { error: unknown }) {
  const tracked = useRef(false)
  if (!tracked.current) {
    trackException(error, { context: 'root-error-boundary' })
    tracked.current = true
  }
  return <ErrorAlert title="An unexpected error occurred" message={deriveErrorMessage(error)} />
})

export const Route = createRootRouteWithContext<{
  queryClient: typeof queryClient
}>()({
  validateSearch: z.object({
    id: z.string().optional(),
    action: z.enum(['activate', 'close-lost']).optional(),
  }),
  component: RootRoute,
  errorComponent: RootErrorComponent,
})
